import { HowToAnswer } from "./HowToAnswer";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";

/**
 * ChoiceBlock — a short block of single-answer questions, each with its own
 * option list, one Continue for the block. Used by the ported v1 Interests /
 * Aptitudes / Values quizzes, where the option lists differ per question and
 * a shared column scale (LikertBlock) would not fit.
 *
 * Options stack full width so a long label never truncates, and every option
 * is a real radio inside a per-question radiogroup.
 */

export interface ChoiceBlockOption {
  label: string;
  emoji?: string;
}

export interface ChoiceBlockQuestion {
  id: string;
  prompt: string;
  options: ChoiceBlockOption[];
  /** Renders options in one row — for short intensity scales. */
  compact?: boolean;
}

export interface ChoiceBlockProps {
  questions: ChoiceBlockQuestion[];
  answers?: Record<string, number>;
  onChange?: (answers: Record<string, number>) => void;
  onComplete: (answers: Record<string, number>) => void;
  ctaLabel?: string;
}

export function ChoiceBlock({
  questions,
  answers: controlled,
  onChange,
  onComplete,
  ctaLabel = "Continue",
}: ChoiceBlockProps) {
  const [local, setLocal] = useState<Record<string, number>>(controlled ?? {});
  const answers = controlled ?? local;
  const done = useMemo(
    () => questions.every((q) => answers[q.id] !== undefined),
    [questions, answers],
  );

  function pick(id: string, index: number) {
    const next = { ...answers, [id]: index };
    setLocal(next);
    onChange?.(next);
  }

  return (
    <div className="space-y-3">
      <HowToAnswer>Choose one answer for each question, then tap Continue.</HowToAnswer>
      {questions.map((q, i) => (
        <motion.div
          key={q.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04, duration: 0.22 }}
          className="rounded-2xl border border-border bg-card p-4 md:p-5"
        >
          <div className="text-base md:text-lg text-foreground/90 mb-3">{q.prompt}</div>
          <div
            role="radiogroup"
            aria-label={q.prompt}
            className={q.compact ? "grid gap-2 grid-cols-2 sm:grid-cols-4" : "grid gap-2"}
          >
            {q.options.map((opt, idx) => {
              const active = answers[q.id] === idx;
              return (
                <button
                  key={`${q.id}-${idx}`}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => pick(q.id, idx)}
                  className={[
                    "flex items-center gap-3 min-h-[48px] rounded-xl border px-4 py-3",
                    "text-left text-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active
                      ? "border-primary bg-primary/15 text-foreground font-medium"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50",
                  ].join(" ")}
                >
                  {opt.emoji && <span aria-hidden="true" className="text-lg">{opt.emoji}</span>}
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      ))}

      <div className="pt-2">
        <button
          type="button"
          disabled={!done}
          onClick={() => onComplete(answers)}
          className={[
            "w-full min-h-[48px] rounded-2xl px-6 text-base font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            done
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-muted text-muted-foreground cursor-not-allowed",
          ].join(" ")}
        >
          {ctaLabel}
        </button>
        {!done && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Answer each one to keep going. There are no wrong answers here.
          </p>
        )}
      </div>
    </div>
  );
}
